// src/components/forms/QuoteForm.tsx

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Collapse,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  ContentCut,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import {
  Client,
  Pet,
  PetType,
  PetSize,
  PetBreed,
  SystemSettings,
  QuoteRequest,
  QuoteDetailedRequest,
  clientsAPI,
  quotesAPI,
  referenceDataAPI,
} from '../../services/api';
import {
  AddRowButton,
  CancelFormButton,
  SaveFormButton,
} from '../common/Buttons';
import { useGlobalToast } from '../../App';
import { useErrorHandler } from '../../errors';
import { useEnterNavigation } from '../../hooks/useEnterNavigation';
import {
  FormWrapper,
  FormContent,
  StickyFooter,
  StickyButtonContainer,
  FormFieldRow,
  FormFieldRowMultiline,
  FieldLabelContainer,
  FieldLabelContainerMultiline,
  FieldLabel,
  FieldInputContainer,
  FieldInputContainerMultiline,
  FieldInputHeader,
  StyledTextField,
  StyledMultilineTextField,
  ResponsiveFormGrid,
  SimilarityDialog,
  TopSpacerRow,
  BottomSpacerRow,
} from './shared';
import { colors } from '../../theme/theme';
import DateRangePicker from './shared/DateRangePicker';
import FieldHelperText from './shared/FieldHelperText';
import BookingSummary from './shared/BookingSummary';
import QuotePetSelectionRow, {
  QuoteSketchPetDraft,
} from './shared/QuotePetSelectionRow';
import PetSelectionRow from './shared/PetSelectionRow';
import SurchargeRow from './shared/SurchargeRow';
import ModalBase from '../common/modal/ModalBase';
import { InputContainer, IncrementButton } from '../common/modal/ModalElements';
import PetStayManager from './shared/modal/PetStayManager';
import PetGroomingManager from './shared/modal/PetGroomingManager';
import SurchargeManager from './shared/modal/SurchargeManager';
import { useBookingFormModals } from './hooks/useBookingFormModals';
import {
  SelectedPet,
  calculateGroomingCount,
  buildBookingPetsPayload,
} from './helpers/groomingHelpers';
import { getCustomIcon } from '../../utils/customIcons';
import { findFirstSimilarClient } from '../../utils/clientSimilarityHelper';
import { SpacerRow } from '../forms/shared/SpacerRow';

interface QuoteFormProps {
  // When supplied, the form skips the prospect block and attaches the
  // quote to this client. Pass from /clients/:id/quote/new.
  existingClient?: Client | null;
  // When supplied, the form runs in edit mode against an existing quote.
  // The rich quote payload comes from bookingsAPI.getById and carries
  // dates, pets, surcharges, kennels (for heater nights), notes.
  existingQuote?: any;
  // Called after a successful create/update so the page can navigate.
  onSuccess?: (bookingId: number) => void;
  // Called when the user clicks Cancel. Defaults to navigate(-1).
  onCancel?: () => void;
}

interface Surcharge {
  id: number;
  reason: string;
  amount: number;
}

// Monotonic counter for sketch-row localIds. The modal hook is typed
// `number | null`, so each draft needs a stable numeric handle distinct
// from the rowId string.
let sketchLocalIdCounter = 1;

const newRow = (): QuoteSketchPetDraft => ({
  rowId: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  localId: sketchLocalIdCounter++,
  petTypeId: '',
  petSizeId: '',
  petBreedId: '',
  petName: '',
});

// Existing-client mode: seed selectedPets from the client's still-owned,
// non-sketch pets. All toggled on by default. Pricing fields default to
// Single rate / no grooming / no discount; the per-pet stay & grooming
// modals fill in detail before submit.
const buildInitialSelectedPets = (client: Client | null): SelectedPet[] =>
  (client?.pets ?? [])
    .filter((p) => p.stillOwned !== false && p.isSketch !== true)
    .map((p) => ({
      petId: p.id,
      petName: p.name,
      petType: p.petType?.type || 'Unknown',
      isSelected: true,
      rateType: 'Single',
      useSharingRate: false,
      groomSmooth: 0,
      groomLong: 0,
      groomCut: 0,
      lineItemDiscountPercentage: 0,
      lineItemDiscount: 0,
    }));

const QuoteForm: React.FC<QuoteFormProps> = ({
  existingClient = null,
  existingQuote = null,
  onSuccess,
  onCancel,
}) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useGlobalToast();
  const { handleError } = useErrorHandler();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isExistingClient = !!existingClient && !existingClient.isProspect;
  const isEditMode = !!existingQuote;
  // A prospect client we already know (fetched via /clients/:id/quote/new)
  // in create mode. Distinct from isExistingClient (real, non-prospect
  // clients) - this quote still needs the sketch-pet builder, but must
  // attach to the existing prospect rather than collecting name/phone/email
  // to create a second, duplicate prospect. Never true in edit mode: an
  // in-progress edit of a prospect's quote always re-collects the prospect
  // fields below, since UpdateQuick requires a `prospect` payload and
  // rejects `clientId` (see QuoteController.UpdateQuick).
  const isKnownProspectClient =
    !!existingClient && existingClient.isProspect === true && !isEditMode;
  // Whether the prospect name/phone/email fields need to be shown,
  // required, checked for similarity, and submitted. True for a brand-new
  // walk-in prospect and for editing an existing prospect's quote; false
  // for a real (non-prospect) client and for a known prospect in create
  // mode, where we already have exactly the right client to attach to.
  const needsProspectDetails = !isExistingClient && !isKnownProspectClient;

  const seedDateString = (iso?: string) =>
    iso ? dayjs(iso).format('YYYY-MM-DD') : '';

  const seedHeaterNights = (() => {
    if (!existingQuote?.kennels?.length) return '0';
    const total = existingQuote.kennels.reduce(
      (sum: number, k: any) => sum + (k.heaterNights || 0),
      0
    );
    return String(total);
  })();

  const [firstName, setFirstName] = useState(
    isEditMode && existingClient ? existingClient.firstName : ''
  );
  const [lastName, setLastName] = useState(
    isEditMode && existingClient ? existingClient.lastName : ''
  );
  const [phone, setPhone] = useState(
    isEditMode && existingClient ? existingClient.phone || '' : ''
  );
  const [email, setEmail] = useState(
    isEditMode && existingClient ? existingClient.email || '' : ''
  );

  const [checkInDate, setCheckInDate] = useState(
    seedDateString(existingQuote?.checkInDate)
  );
  const [checkOutDate, setCheckOutDate] = useState(
    seedDateString(existingQuote?.checkOutDate)
  );
  const [notes, setNotes] = useState(existingQuote?.notes || '');

  // Prospect mode: type/size/breed mini-builder rows
  const [petTypes, setPetTypes] = useState<PetType[]>([]);
  const [petSizes, setPetSizes] = useState<PetSize[]>([]);
  const [petRows, setPetRows] = useState<QuoteSketchPetDraft[]>(() => {
    const clientPets: Pet[] = existingClient?.pets ?? [];
    if (isEditMode && !isExistingClient && existingQuote?.pets?.length) {
      return existingQuote.pets.map((p: any) => {
        const fullPet = clientPets.find((cp: Pet) => cp.id === p.petId);
        const row: QuoteSketchPetDraft = {
          rowId: `row-edit-${p.petId}`,
          localId: sketchLocalIdCounter++,
          petTypeId: fullPet?.petTypeId ?? '',
          petSizeId: fullPet?.petSizeId ?? '',
          petBreedId: fullPet?.petBreedId ?? '',
          petName: p.petName || '',
          rateType: (p.rateType as 'Single' | 'Sharing' | 'Custom') || 'Single',
          customRate: p.dailyRate,
          useSharingRate: p.useSharingRate,
          // numberOfNights intentionally omitted on seed: when the quote dates
          // change the pet should inherit the new booking-level nights rather
          // than carrying a stale value from the previous save. The user can
          // set a custom stay length via the PetStayManager modal if needed.
          groomSmooth: 0,
          groomLong: 0,
          groomCut: 0,
          lineItemDiscountPercentage: p.lineItemDiscountPercentage || 0,
          lineItemDiscount: p.lineItemDiscount || 0,
        };
        return row;
      });
    }
    return isExistingClient ? [] : [newRow()];
  });

  // Existing-client mode: real pets + per-pet pricing detail.
  // In edit mode we seed from existingQuote.pets so the toggles, rates and
  // grooming counts reflect what was last saved.
  const [selectedPets, setSelectedPets] = useState<SelectedPet[]>(() => {
    if (isEditMode && isExistingClient && existingQuote?.pets?.length) {
      const quotePets = existingQuote.pets;
      return (existingClient?.pets ?? [])
        .filter((p) => p.stillOwned !== false && p.isSketch !== true)
        .map((p) => {
          const onQuote = quotePets.find((qp: any) => qp.petId === p.id);
          if (!onQuote) {
            return {
              petId: p.id,
              petName: p.name,
              petType: p.petType?.type || 'Unknown',
              isSelected: false,
              rateType: 'Single' as const,
              useSharingRate: false,
              groomSmooth: 0,
              groomLong: 0,
              groomCut: 0,
              lineItemDiscountPercentage: 0,
              lineItemDiscount: 0,
            };
          }
          return {
            petId: p.id,
            petName: p.name,
            petType: p.petType?.type || 'Unknown',
            isSelected: true,
            rateType:
              (onQuote.rateType as 'Single' | 'Sharing' | 'Custom') || 'Single',
            customRate: onQuote.dailyRate,
            useSharingRate: onQuote.useSharingRate,
            // numberOfNights intentionally omitted on seed: when the quote dates
            // change the pet should inherit the new booking-level nights rather
            // than carrying a stale value from the previous save. The user can
            // set a custom stay length via the PetStayManager modal if needed.
            groomSmooth: 0,
            groomLong: 0,
            groomCut: 0,
            lineItemDiscountPercentage: onQuote.lineItemDiscountPercentage || 0,
            lineItemDiscount: onQuote.lineItemDiscount || 0,
          };
        });
    }
    return buildInitialSelectedPets(existingClient);
  });

  // Existing-client mode: surcharges + heater nights bucket
  const [surcharges, setSurcharges] = useState<Surcharge[]>(() => {
    if (isEditMode && existingQuote?.surcharges?.length) {
      return existingQuote.surcharges.map((s: any, idx: number) => ({
        id: idx + 1,
        reason: s.reason,
        amount: s.amount,
      }));
    }
    return [];
  });
  const [heaterNights, setHeaterNights] = useState<string>(seedHeaterNights);

  // Cache of full Pet records for icon rendering and pricing lookups.
  const availablePets: Pet[] = useMemo(
    () =>
      (existingClient?.pets ?? []).filter(
        (p) => p.stillOwned !== false && p.isSketch !== true
      ),
    [existingClient]
  );

  const numberOfDays = useMemo(() => {
    if (!checkInDate || !checkOutDate) return 0;
    const days = dayjs(checkOutDate).diff(dayjs(checkInDate), 'day');
    return days > 0 ? days : 0;
  }, [checkInDate, checkOutDate]);

  // System settings (heater rate) for the quote summary preview - same
  // endpoint BookingForm already uses.
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(
    null
  );
  const [showSummary, setShowSummary] = useState<boolean>(false);

  // Breed cache keyed by "typeId-sizeId", shared between each sketch row's
  // breed dropdown and the summary preview below - single fetch per
  // distinct type+size pair, regardless of how many rows share it.
  const [breedCache, setBreedCache] = useState<Record<string, PetBreed[]>>({});

  // "Show All" support for each row's Breed field - drops Size scoping,
  // keeps Type (see PetForm's identical rule). Rendered as a "Show All" /
  // "Show Relevant" row appended as the last option in the row's Breed
  // CustomSelect - see QuotePetSelectionRow.
  // breedCacheByType is keyed by typeId alone and only populated for a
  // type once some row actually toggles into "all" mode for it, so the
  // relative-mode common case never pays for the extra fetch.
  const [breedCacheByType, setBreedCacheByType] = useState<
    Record<number, PetBreed[]>
  >({});
  const [showAllBreedsByRow, setShowAllBreedsByRow] = useState<
    Record<string, boolean>
  >({});

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Client similarity (prospect mode only)
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [showClientSimilarityDialog, setShowClientSimilarityDialog] =
    useState(false);
  const [similarClient, setSimilarClient] = useState<Client | null>(null);

  const modal = useBookingFormModals();

  const fieldOrder = useMemo(() => {
    const order: string[] = [];
    if (needsProspectDetails) {
      order.push('firstName', 'lastName', 'phone', 'email');
    }
    order.push('notes', 'submit');
    return order;
  }, [needsProspectDetails]);

  const { registerRef, getKeyHandler, focusFirst } =
    useEnterNavigation(fieldOrder);

  useEffect(() => {
    if (isExistingClient || isEditMode) return;

    const timer = setTimeout(() => {
      focusFirst();
    }, 100);

    return () => clearTimeout(timer);
  }, [isExistingClient, isEditMode, focusFirst]);

  useEffect(() => {
    if (isExistingClient) return;
    referenceDataAPI
      .getPetTypes()
      .then((data) => {
        const ORDER = ['Dog', 'Cat', 'Bird', 'Rodent', 'Reptile', 'Fish'];
        setPetTypes(
          [...data].sort((a, b) => {
            const ai = ORDER.findIndex(
              (t) => t.toLowerCase() === a.type.toLowerCase()
            );
            const bi = ORDER.findIndex(
              (t) => t.toLowerCase() === b.type.toLowerCase()
            );
            return (
              (ai === -1 ? ORDER.length : ai) - (bi === -1 ? ORDER.length : bi)
            );
          })
        );
      })
      .catch((err) => handleError(err, { context: 'loading pet types' }));
  }, [isExistingClient, handleError]);

  // Prospect mode needs full PetSize records so the per-row stay & grooming
  // modals can show Single/Sharing/grooming prices for sketches.
  useEffect(() => {
    if (isExistingClient) return;
    referenceDataAPI
      .getPetSizes()
      .then(setPetSizes)
      .catch((err) => handleError(err, { context: 'loading pet sizes' }));
  }, [isExistingClient, handleError]);

  useEffect(() => {
    if (!needsProspectDetails) return;
    clientsAPI
      .getAll()
      .then(setAllClients)
      .catch((err) =>
        handleError(err, {
          context: 'loading clients for similarity check',
          showToast: false,
        })
      );
  }, [needsProspectDetails, handleError]);

  // System settings (heater rate) for the summary preview. Non-fatal if it
  // fails - the summary simply falls back to a R0.00 heater rate, same as
  // BookingForm's own handling of this endpoint.
  useEffect(() => {
    referenceDataAPI
      .getSystemSettings()
      .then(setSystemSettings)
      .catch(() => {
        // Continue even if system settings fail - summary is a preview,
        // not a blocker.
      });
  }, []);

  // Breed cache for the sketch-pet builder: fetch breeds for every distinct
  // (typeId, sizeId) pair currently present across petRows, once each. Feeds
  // both each row's own breed dropdown and the summary preview below, so the
  // two can never drift out of sync with each other.
  useEffect(() => {
    if (isExistingClient) return;

    const pairs = Array.from(
      new Set(
        petRows
          .filter((r) => r.petTypeId !== '' && r.petSizeId !== '')
          .map((r) => `${r.petTypeId}-${r.petSizeId}`)
      )
    ).filter((key) => !(key in breedCache));

    if (pairs.length === 0) return;

    let cancelled = false;
    Promise.all(
      pairs.map((key) => {
        const [typeId, sizeId] = key.split('-').map(Number);
        return referenceDataAPI
          .getPetBreedsByTypeAndSize(typeId, sizeId)
          .then((data) => [key, data] as const)
          .catch(() => [key, [] as PetBreed[]] as const);
      })
    ).then((results) => {
      if (cancelled) return;
      setBreedCache((prev) => {
        const next = { ...prev };
        results.forEach(([key, data]) => {
          next[key] = data;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [isExistingClient, petRows, breedCache]);

  // Fetch the Type-only breed list for any typeId currently needed by a
  // row in "Show All" mode. Server-side already filters to Active for this
  // endpoint, so no client-side filtering needed here.
  useEffect(() => {
    if (isExistingClient) return;

    const typeIds = Array.from(
      new Set(
        petRows
          .filter((r) => showAllBreedsByRow[r.rowId] && r.petTypeId !== '')
          .map((r) => Number(r.petTypeId))
      )
    ).filter((typeId) => !(typeId in breedCacheByType));

    if (typeIds.length === 0) return;

    let cancelled = false;
    Promise.all(
      typeIds.map((typeId) =>
        referenceDataAPI
          .getPetBreedsByType(typeId)
          .then((data) => [typeId, data] as const)
          .catch(() => [typeId, [] as PetBreed[]] as const)
      )
    ).then((results) => {
      if (cancelled) return;
      setBreedCacheByType((prev) => {
        const next = { ...prev };
        results.forEach(([typeId, data]) => {
          next[typeId] = data;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [isExistingClient, petRows, showAllBreedsByRow, breedCacheByType]);

  // Selector for a single row's breed options - relative (Type+Size) from
  // the shared cache, or Type-only from breedCacheByType when that row is
  // in "Show All" mode.
  const getBreedsForRow = useCallback(
    (typeId: number | '', sizeId: number | '', rowId?: string): PetBreed[] => {
      if (typeId === '') return [];
      if (rowId && showAllBreedsByRow[rowId]) {
        return breedCacheByType[typeId] || [];
      }
      if (sizeId === '') return [];
      return breedCache[`${typeId}-${sizeId}`] || [];
    },
    [breedCache, breedCacheByType, showAllBreedsByRow]
  );

  const handleToggleShowAllBreedsForRow = useCallback((rowId: string) => {
    setShowAllBreedsByRow((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  }, []);

  // When the quote date range changes, reset all per-pet numberOfNights back
  // to undefined so every pet inherits the new booking-level nights on save.
  // This prevents stale values from a previous save surviving a date edit.
  // Edge case: if a pet had a deliberately non-standard stay length the user
  // must re-set it via the PetStayManager modal — this is intentional; it is
  // far less surprising than silently preserving a value that no longer makes
  // sense relative to the new dates.
  useEffect(() => {
    if (!isEditMode) return;
    setSelectedPets((prev) =>
      prev.map((p) =>
        p.numberOfNights !== undefined ? { ...p, numberOfNights: undefined } : p
      )
    );
    setPetRows((prev) =>
      prev.map((r) =>
        r.numberOfNights !== undefined ? { ...r, numberOfNights: undefined } : r
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInDate, checkOutDate]);

  // Pet stay modal. The same handler serves both modes - existing-client
  // updates `selectedPets[]` keyed by real petId; prospect mode updates
  // `petRows[]` keyed by sketch localId.
  const handleEditStayDuration = useCallback(
    (id: number) => {
      modal.openModal('pet-stay', id);
    },
    [modal]
  );

  const getMiddleDividerSx = (borderStyle: string) => ({
    position: 'relative' as const,
    '&::after': {
      content: '""',
      position: 'absolute' as const,
      left: 0,
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      borderBottom: borderStyle,
      pointerEvents: 'none' as const,
      zIndex: 0,
      '@media (max-width: 600px)': {
        display: 'none',
      },
    },
  });

  const handleSaveStayDuration = useCallback(
    (
      duration: number,
      rateType: 'Single' | 'Sharing' | 'Custom',
      customRate?: number,
      lineItemDiscountPercentage?: number
    ) => {
      const editingId = modal.editingItemId;
      if (editingId === null) {
        modal.closeModal();
        return;
      }

      const isSketchRow = petRows.some((r) => r.localId === editingId);

      if (isSketchRow) {
        setPetRows((prev) =>
          prev.map((row) => {
            if (row.localId !== editingId) return row;

            const sizeIdNum = row.petSizeId === '' ? 0 : Number(row.petSizeId);
            const ps = petSizes.find((s) => s.id === sizeIdNum);

            let dailyRate = 0;
            if (rateType === 'Custom' && customRate) {
              dailyRate = customRate;
            } else if (rateType === 'Sharing' && ps?.priceSharing) {
              dailyRate = ps.priceSharing;
            } else if (ps?.priceSingle) {
              dailyRate = ps.priceSingle;
            }

            const staySubtotal = dailyRate * duration;
            const pct = lineItemDiscountPercentage || 0;
            const discountAmount = (staySubtotal * pct) / 100;

            return {
              ...row,
              numberOfNights: duration,
              rateType,
              customRate,
              useSharingRate: rateType === 'Sharing',
              lineItemDiscountPercentage: pct,
              lineItemDiscount: discountAmount,
            };
          })
        );
        modal.closeModal();
        return;
      }

      setSelectedPets((prev) => {
        const updated = prev.map((pet) => {
          if (pet.petId !== editingId) return pet;

          let dailyRate = 0;
          if (rateType === 'Custom' && customRate) {
            dailyRate = customRate;
          } else {
            const petData = availablePets.find((p) => p.id === pet.petId);
            if (rateType === 'Sharing' && petData?.petSize?.priceSharing) {
              dailyRate = petData.petSize.priceSharing;
            } else if (petData?.petSize?.priceSingle) {
              dailyRate = petData.petSize.priceSingle;
            }
          }

          const staySubtotal = dailyRate * duration;
          const discountPercentage = lineItemDiscountPercentage || 0;
          const discountAmount = (staySubtotal * discountPercentage) / 100;

          return {
            ...pet,
            numberOfNights: duration,
            rateType,
            customRate,
            useSharingRate: rateType === 'Sharing',
            lineItemDiscountPercentage: discountPercentage,
            lineItemDiscount: discountAmount,
          };
        });

        if (duration === 0) {
          return updated.filter((p) => p.petId !== editingId);
        }
        return updated;
      });

      modal.closeModal();
    },
    [modal, availablePets, petRows, petSizes]
  );

  // Pet grooming modal. Same dual-dispatch pattern as the stay handler.
  const handleEditGrooming = useCallback(
    (id: number) => {
      modal.openModal('pet-grooming', id);
    },
    [modal]
  );

  const handleSaveGrooming = useCallback(
    (smooth: number, long: number, cut: number) => {
      const editingId = modal.editingItemId;
      if (editingId === null) {
        modal.closeModal();
        return;
      }

      const isSketchRow = petRows.some((r) => r.localId === editingId);

      if (isSketchRow) {
        setPetRows((prev) =>
          prev.map((row) =>
            row.localId === editingId
              ? {
                  ...row,
                  groomSmooth: smooth,
                  groomLong: long,
                  groomCut: cut,
                }
              : row
          )
        );
      } else {
        setSelectedPets((prev) =>
          prev.map((pet) =>
            pet.petId === editingId
              ? { ...pet, groomSmooth: smooth, groomLong: long, groomCut: cut }
              : pet
          )
        );
      }

      modal.closeModal();
    },
    [modal, petRows]
  );

  // Surcharge modal
  const handleOpenSurchargeModal = useCallback(() => {
    modal.openModal('surcharge');
  }, [modal]);

  const handleEditSurcharge = useCallback(
    (id: number) => {
      modal.openModal('surcharge', id);
    },
    [modal]
  );

  const handleSurchargeManagerSave = useCallback(
    (reason: string, amount: number) => {
      if (!reason.trim()) {
        showError('Please enter a reason for the surcharge');
        return;
      }
      if (amount <= 0) {
        showError('Please enter an amount greater than 0');
        return;
      }

      if (modal.editingItemId !== null) {
        setSurcharges((prev) =>
          prev.map((s) =>
            s.id === modal.editingItemId ? { ...s, reason, amount } : s
          )
        );
      } else {
        const newId = Math.max(0, ...surcharges.map((s) => s.id)) + 1;
        setSurcharges((prev) => [...prev, { id: newId, reason, amount }]);
      }

      modal.closeModal();
    },
    [modal, surcharges, showError]
  );

  const handleRemoveSurcharge = useCallback((id: number) => {
    setSurcharges((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const validate = (): boolean => {
    const next: Record<string, string> = {};

    if (needsProspectDetails) {
      if (!firstName.trim()) next.firstName = 'First name required.';
      if (!lastName.trim()) next.lastName = 'Last name required.';
    }

    if (!checkInDate) next.checkInDate = 'Check-in required.';
    if (!checkOutDate) next.checkOutDate = 'Check-out required.';
    if (
      checkInDate &&
      checkOutDate &&
      !dayjs(checkOutDate).isAfter(dayjs(checkInDate))
    ) {
      next.dateRange = 'Check-out must be after check-in.';
    }

    if (isExistingClient) {
      const onCount = selectedPets.filter((p) => p.isSelected !== false).length;
      if (onCount === 0) {
        next.pets = 'Select at least one pet for this quote.';
      }
    } else {
      const completePets = petRows.filter((r) => r.petTypeId && r.petSizeId);
      if (completePets.length === 0) {
        next.pets = 'At least one pet (Type and Size) is required.';
      }
    }

    const heaterValue = parseInt(heaterNights, 10);
    if (heaterNights !== '' && (Number.isNaN(heaterValue) || heaterValue < 0)) {
      next.heaterNights = 'Heater nights must be 0 or greater.';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const completePetCount = useMemo(
    () => petRows.filter((r) => r.petTypeId && r.petSizeId).length,
    [petRows]
  );

  const selectedPetCount = useMemo(
    () => selectedPets.filter((p) => p.isSelected !== false).length,
    [selectedPets]
  );

  // Feeds BookingSummary the same SelectedPet[]/Pet[] shapes it already
  // consumes for real bookings. Existing-client mode already has both in
  // exactly the right shape - no transformation needed, just filtered to
  // the pets actually on this quote (mirrors submitDetailed's own filter).
  // Sketch/prospect mode has no real Pet records yet, so a lightweight
  // view-model Pet is synthesized per row from petSizes + the breed cache;
  // these objects only feed the preview, they are never submitted.
  const summaryPets = useMemo((): {
    selectedPets: SelectedPet[];
    availablePets: Pet[];
  } => {
    if (isExistingClient) {
      return {
        selectedPets: selectedPets.filter((p) => p.isSelected !== false),
        availablePets,
      };
    }

    const completeRows = petRows.filter((r) => r.petTypeId && r.petSizeId);

    const sketchSelectedPets: SelectedPet[] = completeRows.map((r) => ({
      petId: r.localId,
      petName: r.petName || 'Pet',
      petType:
        petTypes.find((t) => t.id === Number(r.petTypeId))?.type || 'Unknown',
      rateType: r.rateType || 'Single',
      customRate: r.customRate,
      numberOfNights: r.numberOfNights,
      groomSmooth: r.groomSmooth,
      groomLong: r.groomLong,
      groomCut: r.groomCut,
      useSharingRate: r.useSharingRate,
      lineItemDiscountPercentage: r.lineItemDiscountPercentage,
      lineItemDiscount: r.lineItemDiscount,
    }));

    const sketchAvailablePets: Pet[] = completeRows.map((r) => {
      const sizeIdNum = Number(r.petSizeId);
      const ps = petSizes.find((s) => s.id === sizeIdNum);
      const breedIdNum = r.petBreedId === '' ? undefined : Number(r.petBreedId);
      const breed = breedIdNum
        ? getBreedsForRow(r.petTypeId, r.petSizeId, r.rowId).find(
            (b) => b.id === breedIdNum
          )
        : undefined;

      return {
        id: r.localId,
        name: r.petName || 'Pet',
        clientId: -1,
        petTypeId: Number(r.petTypeId),
        petSizeId: sizeIdNum,
        petBreedId: breedIdNum,
        petBreed: breed,
        petSize: ps,
        gender: '',
        showWarning: false,
      } as Pet;
    });

    return {
      selectedPets: sketchSelectedPets,
      availablePets: sketchAvailablePets,
    };
  }, [
    isExistingClient,
    selectedPets,
    availablePets,
    petRows,
    petTypes,
    petSizes,
    getBreedsForRow,
  ]);

  const aggregateHeaterNightsValue = useMemo(() => {
    const value = parseInt(heaterNights, 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }, [heaterNights]);

  const handleAddRow = () => setPetRows((prev) => [...prev, newRow()]);
  const handleRemoveRow = (rowId: string) =>
    setPetRows((prev) =>
      prev.length === 1 ? prev : prev.filter((r) => r.rowId !== rowId)
    );
  const handleChangeRow = (next: QuoteSketchPetDraft) => {
    setPetRows((prev) => prev.map((r) => (r.rowId === next.rowId ? next : r)));
    // A Size change (including the implicit reset that happens on a Type
    // change) invalidates that row's "all breeds by Type" list scope, so
    // drop the row back to relative - mirrors PetForm's same reset rule.
    const prevRow = petRows.find((r) => r.rowId === next.rowId);
    if (prevRow && prevRow.petSizeId !== next.petSizeId) {
      setShowAllBreedsByRow((prev) => {
        if (!prev[next.rowId]) return prev;
        const nextState = { ...prev };
        delete nextState[next.rowId];
        return nextState;
      });
    }
  };

  const togglePetSelection = (petId: number) => {
    setSelectedPets((prev) =>
      prev.map((p) =>
        p.petId === petId ? { ...p, isSelected: p.isSelected === false } : p
      )
    );
  };

  const submitDetailed = async () => {
    if (!existingClient) return;

    const activePets = selectedPets.filter((p) => p.isSelected !== false);
    const petsPayload = buildBookingPetsPayload(activePets);

    const heaterValue = parseInt(heaterNights, 10);
    const heaterPayload =
      Number.isFinite(heaterValue) && heaterValue > 0 ? heaterValue : 0;

    const payload: QuoteDetailedRequest = {
      clientId: existingClient.id,
      checkInDate,
      checkOutDate,
      pets: petsPayload,
      surcharges: surcharges.map((s) => ({
        reason: s.reason,
        amount: s.amount,
      })),
      heaterNights: heaterPayload,
      notes: notes.trim() || undefined,
    };

    try {
      setSubmitting(true);
      const result =
        isEditMode && existingQuote
          ? await quotesAPI.updateDetailed(existingQuote.id, payload)
          : await quotesAPI.createDetailed(payload);
      showSuccess();
      if (onSuccess) {
        onSuccess(result.bookingId);
      } else {
        navigate(`/quote/${result.bookingId}`);
      }
    } catch (err) {
      handleError(err, {
        context: isEditMode
          ? 'updating detailed quote'
          : 'creating detailed quote',
        showToast: false,
      });
      showError(
        isEditMode ? 'Could not update quote.' : 'Could not create quote.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitQuick = async () => {
    const heaterValue = parseInt(heaterNights, 10);
    const heaterPayload =
      Number.isFinite(heaterValue) && heaterValue > 0 ? heaterValue : 0;

    const payload: QuoteRequest = {
      checkInDate,
      checkOutDate,
      notes: notes.trim() || undefined,
      ...(isKnownProspectClient
        ? { clientId: existingClient!.id }
        : {
            prospect: {
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              phone: phone.trim() || undefined,
              email: email.trim() || undefined,
            },
          }),
      pets: petRows
        .filter((r) => r.petTypeId && r.petSizeId)
        .map((r) => {
          const groomingTypes: string[] = [
            ...Array(r.groomSmooth || 0).fill('Smooth'),
            ...Array(r.groomLong || 0).fill('Long'),
            ...Array(r.groomCut || 0).fill('Cut'),
          ];
          return {
            petTypeId: Number(r.petTypeId),
            petSizeId: Number(r.petSizeId),
            petBreedId: r.petBreedId === '' ? undefined : Number(r.petBreedId),
            petName: r.petName.trim() || undefined,
            rateType: r.rateType,
            customRate: r.customRate,
            useSharingRate: r.useSharingRate,
            numberOfNights: r.numberOfNights,
            groomingTypes,
            lineItemDiscountPercentage: r.lineItemDiscountPercentage,
            lineItemDiscount: r.lineItemDiscount,
          };
        }),
      surcharges: surcharges.map((s) => ({
        reason: s.reason,
        amount: s.amount,
      })),
      heaterNights: heaterPayload,
    };

    try {
      setSubmitting(true);
      const result =
        isEditMode && existingQuote
          ? await quotesAPI.updateQuick(existingQuote.id, payload)
          : await quotesAPI.createQuick(payload);
      showSuccess();
      if (onSuccess) {
        onSuccess(result.bookingId);
      } else {
        navigate(`/quote/${result.bookingId}`);
      }
    } catch (err) {
      handleError(err, {
        context: isEditMode ? 'updating quote' : 'creating quote',
        showToast: false,
      });
      showError(
        isEditMode ? 'Could not update quote.' : 'Could not create quote.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const proceedWithSubmit = async () => {
    if (isExistingClient) {
      await submitDetailed();
    } else {
      await submitQuick();
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    // Skip similarity check in edit mode (the prospect already exists) and
    // when existingClient already tells us exactly which prospect this is.
    if (needsProspectDetails && !isEditMode) {
      const similar = findFirstSimilarClient(firstName, lastName, allClients);
      if (similar) {
        setSimilarClient(similar);
        setShowClientSimilarityDialog(true);
        return;
      }
    }

    proceedWithSubmit();
  };

  const handleUseExistingClient = () => {
    setShowClientSimilarityDialog(false);
    if (similarClient) {
      navigate(`/clients/${similarClient.id}`);
    }
    setSimilarClient(null);
  };

  const handleCreateNewProspect = () => {
    setShowClientSimilarityDialog(false);
    setSimilarClient(null);
    proceedWithSubmit();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate(-1);
    }
  };

  // Modal renderers. Each branches on whether the editingItemId belongs
  // to a sketch row (prospect mode) or a real pet (existing-client mode).
  const renderPetStayModal = () => {
    if (!modal.isOpen('pet-stay') || modal.editingItemId === null) return null;

    const sketchRow = petRows.find((r) => r.localId === modal.editingItemId);

    if (sketchRow) {
      const sizeIdNum =
        sketchRow.petSizeId === '' ? 0 : Number(sketchRow.petSizeId);
      const ps = petSizes.find((s) => s.id === sizeIdNum);
      const label = `Sketch pet ${
        petRows.findIndex((r) => r.localId === sketchRow.localId) + 1
      }`;

      return (
        <ModalBase
          mobileWidth="92vw"
          open={true}
          onClose={modal.closeModal}
          petName={label}
          headerIcon={<EditIcon />}
          headerTitle={`Edit ${label}`}
        >
          <PetStayManager
            petId={modal.editingItemId}
            rateType={sketchRow.rateType || 'Single'}
            customRate={sketchRow.customRate}
            lineItemDiscount={sketchRow.lineItemDiscountPercentage || 0}
            currentDuration={sketchRow.numberOfNights || numberOfDays}
            maxDuration={numberOfDays}
            bookingLength={numberOfDays}
            priceSingle={ps?.priceSingle}
            priceSharing={ps?.priceSharing}
            onSave={handleSaveStayDuration}
            onCancel={modal.closeModal}
            isLoading={submitting}
          />
        </ModalBase>
      );
    }

    const petData = availablePets.find((p) => p.id === modal.editingItemId);
    const selectedPet = selectedPets.find(
      (p) => p.petId === modal.editingItemId
    );

    return (
      <ModalBase
        mobileWidth="92vw"
        open={true}
        onClose={modal.closeModal}
        petName={selectedPet?.petName || ''}
        headerIcon={<EditIcon />}
        headerTitle={`Edit ${selectedPet?.petName || ''}`}
      >
        <PetStayManager
          petId={modal.editingItemId}
          rateType={selectedPet?.rateType || 'Single'}
          customRate={selectedPet?.customRate}
          lineItemDiscount={selectedPet?.lineItemDiscountPercentage || 0}
          currentDuration={selectedPet?.numberOfNights || numberOfDays}
          maxDuration={numberOfDays}
          bookingLength={numberOfDays}
          priceSingle={petData?.petSize?.priceSingle}
          priceSharing={petData?.petSize?.priceSharing}
          onSave={handleSaveStayDuration}
          onCancel={modal.closeModal}
          isLoading={submitting}
        />
      </ModalBase>
    );
  };

  const renderPetGroomingModal = () => {
    if (!modal.isOpen('pet-grooming') || modal.editingItemId === null)
      return null;

    const sketchRow = petRows.find((r) => r.localId === modal.editingItemId);

    if (sketchRow) {
      const label = `Sketch pet ${
        petRows.findIndex((r) => r.localId === sketchRow.localId) + 1
      }`;
      return (
        <ModalBase
          mobileWidth="92vw"
          open={true}
          onClose={modal.closeModal}
          petName={label}
          headerIcon={<ContentCut />}
          headerTitle={`Groom ${label}`}
        >
          <PetGroomingManager
            petId={modal.editingItemId}
            groomSmooth={sketchRow.groomSmooth || 0}
            groomLong={sketchRow.groomLong || 0}
            groomCut={sketchRow.groomCut || 0}
            onSave={handleSaveGrooming}
            onCancel={modal.closeModal}
            isLoading={submitting}
          />
        </ModalBase>
      );
    }

    const selectedPet = selectedPets.find(
      (p) => p.petId === modal.editingItemId
    );

    return (
      <ModalBase
        mobileWidth="92vw"
        open={true}
        onClose={modal.closeModal}
        petName={selectedPet?.petName || ''}
        headerIcon={<ContentCut />}
        headerTitle={`Groom ${selectedPet?.petName || ''}`}
      >
        <PetGroomingManager
          petId={modal.editingItemId}
          groomSmooth={selectedPet?.groomSmooth || 0}
          groomLong={selectedPet?.groomLong || 0}
          groomCut={selectedPet?.groomCut || 0}
          onSave={handleSaveGrooming}
          onCancel={modal.closeModal}
          isLoading={submitting}
        />
      </ModalBase>
    );
  };

  const renderSurchargeModal = () => (
    <ModalBase
      open={modal.isOpen('surcharge')}
      onClose={modal.closeModal}
      mobileWidth="92vw"
      petName="Surcharge"
      headerIcon={getCustomIcon('money')}
      headerTitle={
        modal.editingItemId !== null ? 'Edit Surcharge' : 'Add Surcharge'
      }
    >
      <SurchargeManager
        currentReason={
          modal.editingItemId !== null
            ? surcharges.find((s) => s.id === modal.editingItemId)?.reason || ''
            : ''
        }
        currentAmount={
          modal.editingItemId !== null
            ? surcharges.find((s) => s.id === modal.editingItemId)?.amount || 0
            : 0
        }
        onSave={handleSurchargeManagerSave}
        onCancel={modal.closeModal}
        isLoading={submitting}
      />
    </ModalBase>
  );

  // Stay-dates row. Prospect mode renders this AFTER the name/phone/email
  // fields so the form reads like an onboarding form top-down. Existing-
  // client mode keeps dates at the top.
  const dateRangeRow = (
    <FormFieldRowMultiline>
      <FieldLabelContainerMultiline>
        <FieldLabel>
          {isMobile ? (
            <>
              Check-in / Check-out
              <span className="required">*</span>
            </>
          ) : (
            <Box
              component="span"
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <Box component="span" height={'3.3rem'}>
                Check-in<span className="required">*</span>
              </Box>
              <Box component="span" height={'1rem'}></Box>
              <Box component="span" height={'3.3rem'}>
                Check-out<span className="required">*</span>
              </Box>
            </Box>
          )}
        </FieldLabel>
      </FieldLabelContainerMultiline>
      <FieldInputContainerMultiline sx={{ paddingLeft: '1rem' }}>
        <DateRangePicker
          mode="range"
          desktopType="modal"
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          onCheckInChange={setCheckInDate}
          onCheckOutChange={setCheckOutDate}
          checkInError={Boolean(errors.checkInDate)}
          checkInHelperText={errors.checkInDate}
          checkOutError={Boolean(errors.checkOutDate)}
          checkOutHelperText={errors.checkOutDate}
          rangeError={Boolean(errors.dateRange)}
          rangeHelperText={errors.dateRange}
          disabled={submitting}
        />
      </FieldInputContainerMultiline>
    </FormFieldRowMultiline>
  );

  const areDatesSelected = !!(checkInDate && checkOutDate);

  const lengthRow = (
    <FormFieldRow>
      <FieldLabelContainer>
        <FieldLabel sx={{ display: { xs: 'none', sm: 'block' } }}>
          Length
        </FieldLabel>
      </FieldLabelContainer>
      <FieldInputContainer sx={{ paddingLeft: '1rem' }}>
        <Typography
          sx={{
            fontSize: '1.8rem',
            fontWeight: 700,
            color: areDatesSelected ? 'text.primary' : 'text.disabled',
            display: { xs: 'block', sm: 'none' },
          }}
        >
          Length: {numberOfDays} {numberOfDays === 1 ? 'Day' : 'Days'}
        </Typography>
        <Typography
          sx={{
            fontSize: '1.8rem',
            fontWeight: 700,
            color: areDatesSelected ? 'text.primary' : 'text.disabled',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {numberOfDays} {numberOfDays === 1 ? 'Day' : 'Days'}
        </Typography>
      </FieldInputContainer>
    </FormFieldRow>
  );

  return (
    <FormWrapper>
      <FormContent>
        <ResponsiveFormGrid
          breakpointPadding={{
            default: '0.25rem',
            medium: '0.1rem',
            large: '0.1rem',
          }}
          container
          spacing={0}
        >
          <TopSpacerRow datesSelected={true} />

          {isExistingClient && dateRangeRow}
          {isExistingClient && lengthRow}

          {!isExistingClient && (
            <>
              {needsProspectDetails && (
                <>
                  <FormFieldRow>
                    <FieldLabelContainer>
                      <FieldLabel id="quote-prospect-firstName-label">
                        First Name <span className="required">*</span>
                      </FieldLabel>
                    </FieldLabelContainer>
                    <FieldInputContainer
                      sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
                    >
                      <StyledTextField
                        id="quote-prospect-firstName"
                        inputProps={{
                          'aria-labelledby': 'quote-prospect-firstName-label',
                          autoComplete: 'new-password',
                        }}
                        fullWidth
                        value={firstName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFirstName(e.target.value)
                        }
                        error={Boolean(errors.firstName)}
                        helperText={errors.firstName}
                        disabled={submitting}
                        inputRef={registerRef('firstName')}
                        onKeyDown={getKeyHandler('firstName')}
                        autoComplete="off"
                      />
                    </FieldInputContainer>
                  </FormFieldRow>

                  <FormFieldRow>
                    <FieldLabelContainer>
                      <FieldLabel id="quote-prospect-lastName-label">
                        Last Name <span className="required">*</span>
                      </FieldLabel>
                    </FieldLabelContainer>
                    <FieldInputContainer
                      sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
                    >
                      <StyledTextField
                        id="quote-prospect-lastName"
                        inputProps={{
                          'aria-labelledby': 'quote-prospect-lastName-label',
                          autoComplete: 'new-password',
                        }}
                        fullWidth
                        value={lastName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setLastName(e.target.value)
                        }
                        error={Boolean(errors.lastName)}
                        helperText={errors.lastName}
                        disabled={submitting}
                        inputRef={registerRef('lastName')}
                        onKeyDown={getKeyHandler('lastName')}
                        autoComplete="off"
                      />
                    </FieldInputContainer>
                  </FormFieldRow>

                  <FormFieldRow>
                    <FieldLabelContainer>
                      <FieldLabel id="quote-prospect-phone-label">
                        Phone
                      </FieldLabel>
                    </FieldLabelContainer>
                    <FieldInputContainer
                      sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
                    >
                      <StyledTextField
                        id="quote-prospect-phone"
                        inputProps={{
                          'aria-labelledby': 'quote-prospect-phone-label',
                          autoComplete: 'new-password',
                        }}
                        fullWidth
                        value={phone}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setPhone(e.target.value)
                        }
                        error={Boolean(errors.phone)}
                        helperText={errors.phone}
                        disabled={submitting}
                        inputRef={registerRef('phone')}
                        onKeyDown={getKeyHandler('phone')}
                        autoComplete="off"
                      />
                    </FieldInputContainer>
                  </FormFieldRow>

                  <FormFieldRow>
                    <FieldLabelContainer>
                      <FieldLabel id="quote-prospect-email-label">
                        Email
                      </FieldLabel>
                    </FieldLabelContainer>
                    <FieldInputContainer
                      sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
                    >
                      <StyledTextField
                        id="quote-prospect-email"
                        inputProps={{
                          'aria-labelledby': 'quote-prospect-email-label',
                          autoComplete: 'new-password',
                        }}
                        fullWidth
                        type="email"
                        value={email}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEmail(e.target.value)
                        }
                        error={Boolean(errors.email)}
                        helperText={errors.email}
                        disabled={submitting}
                        inputRef={registerRef('email')}
                        onKeyDown={getKeyHandler('email')}
                        autoComplete="off"
                      />
                    </FieldInputContainer>
                  </FormFieldRow>

                  <SpacerRow
                    height="2rem"
                    labelHeightMobile="1rem"
                    valueHeightMobile="1rem"
                    labelBorderMobile={{
                      top: 'none',
                      bottom: 'none',
                    }}
                    valueSx={getMiddleDividerSx(
                      `3px dashed ${theme.palette.dividerContrast}`
                    )}
                    valueBorderMobile={{
                      top: `3px dashed ${theme.palette.dividerContrast}`,
                      bottom: 'none',
                    }}
                    labelBackgroundColor={
                      theme.palette.mode === 'dark'
                        ? theme.palette.grey[750]
                        : theme.palette.grey[400]
                    }
                    labelBackgroundColorMobile={
                      theme.palette.mode === 'dark'
                        ? theme.palette.grey[750]
                        : theme.palette.grey[300]
                    }
                  />
                </>
              )}

              {/* Dates section header */}
              <FormFieldRow>
                <FieldLabelContainer />
                <FieldInputContainer sx={{ '&&': { padding: '0.15rem 0' } }}>
                  <FieldInputHeader
                    sx={{
                      backgroundColor: `${theme.palette.primary.main}`,
                      padding: '0.125rem 1rem 0.125rem 0',
                      [theme.breakpoints.down('sm')]: {
                        padding: '0.125rem 0',
                      },
                    }}
                  >
                    <FieldLabel
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        paddingLeft: '1rem',
                        color: colors.text.light,
                      }}
                    >
                      Dates
                    </FieldLabel>
                  </FieldInputHeader>
                </FieldInputContainer>
              </FormFieldRow>

              {dateRangeRow}
              {lengthRow}
            </>
          )}

          <SpacerRow
            height="2rem"
            labelHeightMobile="1rem"
            valueHeightMobile="1rem"
            labelBorderMobile={{
              top: 'none',
              bottom: 'none',
            }}
            valueSx={getMiddleDividerSx(
              `3px dashed ${theme.palette.dividerContrast}`
            )}
            valueBorderMobile={{
              top: `3px dashed ${theme.palette.dividerContrast}`,
              bottom: 'none',
            }}
            labelBackgroundColor={
              theme.palette.mode === 'dark'
                ? theme.palette.grey[750]
                : theme.palette.grey[400]
            }
            labelBackgroundColorMobile={
              theme.palette.mode === 'dark'
                ? theme.palette.grey[750]
                : theme.palette.grey[300]
            }
          />

          {/* Pets section header */}
          <FormFieldRow>
            <FieldLabelContainer />
            <FieldInputContainer sx={{ '&&': { padding: '0.15rem 0' } }}>
              <FieldInputHeader
                sx={{
                  backgroundColor: `${theme.palette.primary.main}`,
                  padding: '0.125rem 1rem 0.125rem 0',
                  [theme.breakpoints.down('sm')]: {
                    padding: '0.125rem 0',
                  },
                }}
              >
                <FieldLabel
                  sx={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    paddingLeft: '1rem',
                    color: colors.text.light,
                  }}
                >
                  Pets<span className="requiredLight">*</span>
                </FieldLabel>
                <FieldLabel
                  sx={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    paddingRight: '1rem',
                    color: colors.text.light,
                    fontStyle:
                      selectedPetCount > 0 || completePetCount > 0
                        ? 'unset'
                        : 'italic',
                    opacity:
                      selectedPetCount > 0 || completePetCount > 0
                        ? 'unset'
                        : 0.75,
                  }}
                >
                  {isExistingClient ? (
                    <>{selectedPetCount} selected</>
                  ) : (
                    <>{completePetCount} included</>
                  )}
                </FieldLabel>
              </FieldInputHeader>
            </FieldInputContainer>
          </FormFieldRow>

          {/* Pets */}
          <FormFieldRow>
            <FieldLabelContainer />
            <FieldInputContainer
              sx={{
                paddingLeft: '1rem',
                maxWidth: '800px',
              }}
            >
              {isExistingClient ? (
                <>
                  <Box>
                    {availablePets.map((pet) => {
                      const sel = selectedPets.find((s) => s.petId === pet.id);
                      const isSelected = sel ? sel.isSelected !== false : false;
                      const groomingCount = sel
                        ? calculateGroomingCount(sel)
                        : 0;
                      const stayLength = sel?.numberOfNights;

                      return (
                        <PetSelectionRow
                          key={pet.id}
                          petId={pet.id}
                          petName={pet.name}
                          petType={pet.petType?.type || 'Unknown'}
                          petBreed={pet.petBreed?.breed}
                          isSelected={isSelected}
                          onToggle={togglePetSelection}
                          disabled={submitting}
                          numberOfNights={stayLength}
                          bookingLength={numberOfDays}
                          useSharingRate={sel?.useSharingRate || false}
                          useCustomRate={sel?.rateType === 'Custom'}
                          groomingCount={groomingCount}
                          stayLength={stayLength}
                          priceSingle={pet.petSize?.priceSingle}
                          priceSharing={pet.petSize?.priceSharing}
                          priceGroomingSmooth={pet.petSize?.priceGroomingSmooth}
                          priceGroomingLong={pet.petSize?.priceGroomingLong}
                          priceCut={pet.petSize?.priceCut}
                          discountPercentage={
                            sel?.lineItemDiscountPercentage || 0
                          }
                          onEditClick={handleEditStayDuration}
                          onGroomingClick={handleEditGrooming}
                        />
                      );
                    })}
                  </Box>

                  {availablePets.length === 0 && (
                    <FieldHelperText helperText="This client has no still-owned pets to quote on." />
                  )}
                </>
              ) : (
                <>
                  <Box sx={{ maxWidth: '600px' }}>
                    {petRows.map((row, idx) => (
                      <QuotePetSelectionRow
                        key={row.rowId}
                        draft={row}
                        index={idx}
                        petTypes={petTypes}
                        breeds={getBreedsForRow(
                          row.petTypeId,
                          row.petSizeId,
                          row.rowId
                        )}
                        showAllBreeds={Boolean(showAllBreedsByRow[row.rowId])}
                        onToggleShowAllBreeds={() =>
                          handleToggleShowAllBreedsForRow(row.rowId)
                        }
                        showRemove={petRows.length > 1}
                        bookingLength={numberOfDays}
                        onChange={handleChangeRow}
                        onRemove={() => handleRemoveRow(row.rowId)}
                        onEditClick={handleEditStayDuration}
                        onGroomingClick={handleEditGrooming}
                      />
                    ))}
                  </Box>

                  {petRows.some((r) => r.petTypeId && r.petSizeId) && (
                    <Box
                      sx={{
                        display: 'flex',
                        width: '100%',
                        maxWidth: '800px',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        mt: 1,
                      }}
                    >
                      <Typography
                        onClick={handleAddRow}
                        sx={{
                          color: 'primary.main',
                          fontSize: '1.8rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          paddingRight: '1rem',
                          '&:hover': {
                            color: 'primary.dark',
                          },
                        }}
                      >
                        Add Pet
                      </Typography>
                      <AddRowButton
                        onClick={handleAddRow}
                        disabled={submitting}
                        title="Add pet row"
                      />
                    </Box>
                  )}
                </>
              )}

              {errors.pets && (
                <FieldHelperText error helperText={errors.pets} />
              )}
            </FieldInputContainer>
          </FormFieldRow>

          <SpacerRow
            height="2rem"
            labelHeightMobile="1rem"
            valueHeightMobile="1rem"
            labelBorderMobile={{
              top: 'none',
              bottom: 'none',
            }}
            valueSx={getMiddleDividerSx(
              `3px dashed ${theme.palette.dividerContrast}`
            )}
            valueBorderMobile={{
              top: `3px dashed ${theme.palette.dividerContrast}`,
              bottom: 'none',
            }}
            labelBackgroundColor={
              theme.palette.mode === 'dark'
                ? theme.palette.grey[750]
                : theme.palette.grey[400]
            }
            labelBackgroundColorMobile={
              theme.palette.mode === 'dark'
                ? theme.palette.grey[750]
                : theme.palette.grey[300]
            }
          />

          {/* Heaters section header */}
          <FormFieldRow>
            <FieldLabelContainer />
            <FieldInputContainer sx={{ '&&': { padding: '0.15rem 0' } }}>
              <FieldInputHeader
                sx={{
                  backgroundColor: `${theme.palette.primary.main}`,
                  padding: '0.125rem 1rem 0.125rem 0',
                  [theme.breakpoints.down('sm')]: {
                    padding: '0.125rem 0',
                  },
                }}
              >
                <FieldLabel
                  sx={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    paddingLeft: '1rem',
                    color: colors.text.light,
                  }}
                >
                  Heaters
                </FieldLabel>
              </FieldInputHeader>
            </FieldInputContainer>
          </FormFieldRow>

          {/* Heater Nights */}
          <FormFieldRow>
            <FieldLabelContainer />
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <InputContainer sx={{ maxWidth: '24rem' }}>
                <IncrementButton
                  onClick={() => {
                    const current = parseInt(heaterNights, 10);
                    const next = Number.isFinite(current) ? current - 1 : 0;
                    setHeaterNights(String(Math.max(0, next)));
                  }}
                  disabled={
                    submitting || (parseInt(heaterNights, 10) || 0) <= 0
                  }
                  title="Decrease heater nights"
                >
                  <RemoveIcon />
                </IncrementButton>

                <StyledTextField
                  type="number"
                  value={heaterNights}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setHeaterNights('');
                      return;
                    }
                    const n = parseInt(raw, 10);
                    if (Number.isFinite(n) && n >= 0 && n <= 365) {
                      setHeaterNights(String(n));
                    }
                  }}
                  error={Boolean(errors.heaterNights)}
                  helperText={errors.heaterNights}
                  disabled={submitting}
                  inputProps={{
                    min: 0,
                    max: 365,
                    step: 1,
                    style: { textAlign: 'center' },
                  }}
                  sx={{ flex: 1, maxWidth: '14rem' }}
                />

                <IncrementButton
                  onClick={() => {
                    const current = parseInt(heaterNights, 10);
                    const next = Number.isFinite(current) ? current + 1 : 1;
                    setHeaterNights(String(Math.min(365, next)));
                  }}
                  disabled={
                    submitting || (parseInt(heaterNights, 10) || 0) >= 365
                  }
                  title="Increase heater nights"
                >
                  <AddIcon />
                </IncrementButton>
              </InputContainer>
            </FieldInputContainer>
          </FormFieldRow>

          <SpacerRow
            height="2rem"
            labelHeightMobile="1rem"
            valueHeightMobile="1rem"
            labelBorderMobile={{
              top: 'none',
              bottom: 'none',
            }}
            valueSx={getMiddleDividerSx(
              `3px dashed ${theme.palette.dividerContrast}`
            )}
            valueBorderMobile={{
              top: `3px dashed ${theme.palette.dividerContrast}`,
              bottom: 'none',
            }}
            labelBackgroundColor={
              theme.palette.mode === 'dark'
                ? theme.palette.grey[750]
                : theme.palette.grey[400]
            }
            labelBackgroundColorMobile={
              theme.palette.mode === 'dark'
                ? theme.palette.grey[750]
                : theme.palette.grey[300]
            }
          />
          {/* Surcharges section header */}
          <FormFieldRow>
            <FieldLabelContainer />
            <FieldInputContainer sx={{ '&&': { padding: '0.15rem 0' } }}>
              <FieldInputHeader
                sx={{
                  backgroundColor: `${theme.palette.primary.main}`,
                  padding: '0.125rem 1rem 0.125rem 0',
                  [theme.breakpoints.down('sm')]: {
                    padding: '0.125rem 0',
                  },
                }}
              >
                <FieldLabel
                  sx={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    paddingLeft: '1rem',
                    color: colors.text.light,
                  }}
                >
                  Surcharges
                </FieldLabel>
              </FieldInputHeader>
            </FieldInputContainer>
          </FormFieldRow>

          {/* Surcharges */}
          <FormFieldRow>
            <FieldLabelContainer />
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '800px' }}
            >
              <Box>
                {surcharges.map((s) => (
                  <SurchargeRow
                    key={s.id}
                    surchargeId={s.id}
                    reason={s.reason}
                    amount={s.amount}
                    onEdit={handleEditSurcharge}
                    onDelete={handleRemoveSurcharge}
                    disabled={submitting}
                  />
                ))}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  maxWidth: '800px',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  mt: 1,
                }}
              >
                <Typography
                  onClick={handleOpenSurchargeModal}
                  sx={{
                    color: 'primary.main',
                    fontSize: '1.8rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    paddingRight: '1rem',
                    '&:hover': {
                      color: 'primary.dark',
                    },
                  }}
                >
                  Add Surcharge
                </Typography>
                <AddRowButton
                  onClick={handleOpenSurchargeModal}
                  disabled={submitting}
                  title="Add surcharge"
                />
              </Box>
            </FieldInputContainer>
          </FormFieldRow>

          <SpacerRow
            height="2rem"
            labelHeightMobile="1rem"
            valueHeightMobile="1rem"
            labelBorderMobile={{
              top: 'none',
              bottom: 'none',
            }}
            valueSx={getMiddleDividerSx(
              `3px dashed ${theme.palette.dividerContrast}`
            )}
            valueBorderMobile={{
              top: `3px dashed ${theme.palette.dividerContrast}`,
              bottom: 'none',
            }}
            labelBackgroundColor={
              theme.palette.mode === 'dark'
                ? theme.palette.grey[750]
                : theme.palette.grey[400]
            }
            labelBackgroundColorMobile={
              theme.palette.mode === 'dark'
                ? theme.palette.grey[750]
                : theme.palette.grey[300]
            }
          />

          {/* Notes section header */}
          <FormFieldRow>
            <FieldLabelContainer />
            <FieldInputContainer sx={{ '&&': { padding: '0.15rem 0' } }}>
              <FieldInputHeader
                sx={{
                  backgroundColor: `${theme.palette.primary.main}`,
                  padding: '0.125rem 1rem 0.125rem 0',
                  [theme.breakpoints.down('sm')]: {
                    padding: '0.125rem 0',
                  },
                }}
              >
                <FieldLabel
                  sx={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    paddingLeft: '1rem',
                    color: colors.text.light,
                  }}
                >
                  Notes
                </FieldLabel>
              </FieldInputHeader>
            </FieldInputContainer>
          </FormFieldRow>

          {/* Notes */}
          <FormFieldRow>
            <FieldLabelContainerMultiline />
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <StyledMultilineTextField
                fullWidth
                multiline
                rows={3}
                value={notes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setNotes(e.target.value)
                }
                placeholder=""
                disabled={submitting}
                inputRef={registerRef('notes')}
                onKeyDown={getKeyHandler('notes')}
              />
            </FieldInputContainer>
          </FormFieldRow>

          <SpacerRow
            height="2rem"
            labelHeightMobile="1rem"
            valueHeightMobile="1rem"
            labelBorderMobile={{
              top: 'none',
              bottom: 'none',
            }}
            valueSx={getMiddleDividerSx(
              `3px dashed ${theme.palette.dividerContrast}`
            )}
            valueBorderMobile={{
              top: `3px dashed ${theme.palette.dividerContrast}`,
              bottom: 'none',
            }}
            labelBackgroundColor={
              theme.palette.mode === 'dark'
                ? theme.palette.grey[750]
                : theme.palette.grey[400]
            }
            labelBackgroundColorMobile={
              theme.palette.mode === 'dark'
                ? theme.palette.grey[750]
                : theme.palette.grey[300]
            }
          />

          {/* Quote Summary Toggle & Display */}
          <Collapse in={!showSummary} timeout={300} sx={{ width: '100%' }}>
            <FormFieldRow>
              <FieldLabelContainer />
              <FieldInputContainer>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '800px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Typography
                      onClick={() => setShowSummary(true)}
                      sx={{
                        color: 'primary.main',
                        fontSize: '1.8rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: 'secondary.main',
                        },
                      }}
                    >
                      Quote Summary
                    </Typography>
                  </Box>
                </Box>
              </FieldInputContainer>
            </FormFieldRow>
          </Collapse>

          <Collapse in={showSummary} timeout={300} sx={{ width: '100%' }}>
            <FormFieldRow>
              <FieldLabelContainer />
              <FieldInputContainer>
                <BookingSummary
                  checkInDate={checkInDate}
                  checkOutDate={checkOutDate}
                  numberOfDays={numberOfDays}
                  selectedPets={summaryPets.selectedPets}
                  availablePets={summaryPets.availablePets}
                  aggregateHeaterNights={aggregateHeaterNightsValue}
                  surcharges={surcharges}
                  notes={notes}
                  status="Quotation"
                  systemSettings={systemSettings}
                  onClose={() => setShowSummary(false)}
                />
              </FieldInputContainer>
            </FormFieldRow>
          </Collapse>

          <BottomSpacerRow />
        </ResponsiveFormGrid>
      </FormContent>

      <StickyFooter>
        <StickyButtonContainer>
          <CancelFormButton
            minWidth="278px"
            onClick={handleCancel}
            disabled={submitting}
            sx={{
              minWidth: { xs: '175px', sm: '278px' },
            }}
          />
          <SaveFormButton
            text={
              submitting
                ? isEditMode
                  ? 'Updating...'
                  : 'Creating...'
                : isEditMode
                  ? 'Update'
                  : 'Create'
            }
            onClick={handleSubmit}
            disabled={submitting}
            minWidth="278px"
            sx={{
              minWidth: { xs: '165px', sm: '278px' },
            }}
          />
        </StickyButtonContainer>
      </StickyFooter>

      <SimilarityDialog
        open={showClientSimilarityDialog}
        entityType="client"
        similarEntity={similarClient}
        isEditMode={false}
        onUseExisting={handleUseExistingClient}
        onCreateNew={handleCreateNewProspect}
        onClose={() => setShowClientSimilarityDialog(false)}
      />

      {/* Pet stay & grooming modals serve both modes; the renderers
          internally branch on whether the editingItemId belongs to a
          sketch row or a real selectedPet. */}
      {renderPetStayModal()}
      {renderPetGroomingModal()}

      {renderSurchargeModal()}
    </FormWrapper>
  );
};

export default QuoteForm;
